import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { streamAI, getSessions, getMessages, chatToApproval } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Robot, PaperPlaneRight, Plus, ChatCircleDots, Sparkle, Flame, FacebookLogo, InstagramLogo, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "What should I focus on this week to grow sales?",
  "Draft a 3-email abandoned cart sequence",
  "Suggest a bundle to lift my average order value",
  "Write 3 Meta ad hooks for my best seller",
];

export default function AiManagerPage() {
  const { activeId, activeClient } = useClient();
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const { data: sessions = [] } = useQuery({ queryKey: ["sessions", activeId], queryFn: () => getSessions(activeId), enabled: !!activeId });

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const loadSession = async (id) => {
    setSessionId(id);
    const msgs = await getMessages(id);
    setMessages(msgs);
  };
  const newChat = () => { setSessionId(null); setMessages([]); };

  const [sentIdx, setSentIdx] = useState(() => new Set());
  const sendToQueue = async (idx, content, platform) => {
    try {
      await chatToApproval(activeId, content, platform);
      setSentIdx((s) => new Set(s).add(`${idx}-${platform}`));
      toast.success(`Sent to Approval Queue (${platform}) — review & publish there`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send to queue");
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    let sid = sessionId;
    try {
      await streamAI("/ai/chat-stream", { client_id: activeId, session_id: sessionId, message: msg },
        (chunk) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + chunk }; return c; }),
        (meta) => { if (meta.session_id) sid = meta.session_id; });
    } catch (e) {
      const errMsg = e?.message || "Sorry, something went wrong. Please try again.";
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: errMsg }; return c; });
      toast.error(errMsg);
    }
    setBusy(false);
    if (sid && sid !== sessionId) setSessionId(sid);
    qc.invalidateQueries({ queryKey: ["sessions", activeId] });
  };

  return (
    <div className="flex h-full">
      {/* Sessions */}
      <div className="hidden md:flex w-64 shrink-0 flex-col border-r border-white/10 bg-[#0a0a0a]">
        <div className="p-4">
          <Button data-testid="new-chat-btn" onClick={newChat} className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Plus size={16} className="mr-1.5" /> New chat</Button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 space-y-1">
          {sessions.map((s) => (
            <button key={s.id} data-testid="session-item" onClick={() => loadSession(s.id)}
              className={`flex items-center gap-2 w-full text-left px-3 py-2.5 rounded-lg text-sm truncate ${sessionId === s.id ? "bg-white/8 text-white" : "text-[#9a9a9a] hover:bg-white/5"}`}>
              <ChatCircleDots size={15} className="shrink-0" /> <span className="truncate">{s.title || "Conversation"}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 flex flex-col h-full">
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <PageContainer>
            {messages.length === 0 ? (
              <div className="max-w-2xl mx-auto text-center py-12 fade-up">
                <div className="h-16 w-16 rounded-2xl bg-[#6B3DFF]/15 flex items-center justify-center mx-auto mb-4 brand-glow">
                  <Robot size={32} className="brand-text" weight="fill" />
                </div>
                <h1 className="font-head text-2xl font-semibold">AI Marketing Manager</h1>
                <p className="text-[#9a9a9a] mt-2">Your strategist for {activeClient?.name}. Ask about strategy, campaigns, copy or what to do next.</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-8 text-left">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} data-testid={`suggestion-${i}`} onClick={() => send(s)}
                      className="card-matte p-4 text-sm text-[#cfcfcf] hover:text-white flex items-center gap-2">
                      <Sparkle size={16} className="brand-text shrink-0" weight="fill" /> {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-5">
                {messages.map((m, i) => (
                  <div key={i} data-testid={`msg-${m.role}`} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                    {m.role === "assistant" && <div className="h-8 w-8 rounded-lg bg-[#6B3DFF]/15 flex items-center justify-center shrink-0"><Flame size={16} className="brand-text" weight="fill" /></div>}
                    <div className={`max-w-[80%] ${m.role === "user" ? "" : "flex-1"}`}>
                      <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#6B3DFF] text-white ml-auto w-fit" : "bg-[#161616] border border-white/8 text-[#e6e6e6]"}`}>
                        {m.content || <span className="ai-thinking text-[#9a9a9a]">Thinking…</span>}
                      </div>
                      {m.role === "assistant" && m.content && !busy && (
                        <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid={`send-queue-row-${i}`}>
                          <span className="text-[11px] text-[#7a7a7a]">Send to Approval Queue:</span>
                          <button data-testid={`send-fb-${i}`} onClick={() => sendToQueue(i, m.content, "Facebook")}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-sky-400">
                            {sentIdx.has(`${i}-Facebook`) ? <CheckCircle size={12} weight="fill" /> : <FacebookLogo size={12} />} Facebook
                          </button>
                          <button data-testid={`send-ig-${i}`} onClick={() => sendToQueue(i, m.content, "Instagram")}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-pink-400">
                            {sentIdx.has(`${i}-Instagram`) ? <CheckCircle size={12} weight="fill" /> : <InstagramLogo size={12} />} Instagram
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </PageContainer>
        </div>

        <div className="border-t border-white/10 bg-[#0a0a0a] p-4">
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <Textarea data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder="Ask your AI marketing manager…" className="bg-[#10101A] border-white/10 resize-none min-h-[48px]" />
            <Button data-testid="send-btn" onClick={() => send()} disabled={busy} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-12 w-12 p-0 shrink-0">
              <PaperPlaneRight size={18} weight="fill" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
